import { useState } from "react";
import { observer } from "mobx-react-lite";
import { ChevronRightIcon, ChevronLeftIcon } from "lucide-react";
import type { Catalog } from "@repo/fetch-repository/catalogs";
import { Banner } from "../../nodes/banner";
import { Download } from "../icons/download";
import { Search } from "../icons/search";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../ui/dialog";

export interface CatalogsCarouselProps {
  catalogs: Catalog[];
  title?: string;
  banner: string;
  view: string;
  download: string;
  marginBottom?: string;
  marginTop?: string;
}

export const CatalogsCarouselWithBanner = observer(
  ({
    catalogs,
    title,
    banner,
    view,
    download,
    marginBottom,
    marginTop,
  }: CatalogsCarouselProps) => {
    const [selectedIndex, setSelectedIndex] = useState<number>(0);
    const setNextProject = (): void => {
      selectedIndex === catalogs.length - 1
        ? setSelectedIndex(0)
        : setSelectedIndex(selectedIndex + 1);
    };
    const setPreviousProject = (): void => {
      selectedIndex === 0
        ? setSelectedIndex(catalogs.length - 1)
        : setSelectedIndex(selectedIndex - 1);
    };

    return (
      <div className="w-full lg:min-h-72 min-h-32 relative flex items-center">
        {title ? (
          <div className="absolute rtl:right-0 ltr:left-0 ltr:lg:-bottom-2/3 ltr:-bottom-1/3 w-[13%] h-full">
            <div className="size-full relative flex items-center justify-center">
              <div className="absolute rtl:-rotate-90 ltr:-rotate-90 font-thin opacity-50 lg:text-2xl text-xl px-2 text-white before:content-[''] before:inline-block before:bg-white before:h-px before:lg:w-14 before:w-10 after:content-[''] after:inline-block after:bg-white after:h-px after:lg:w-14 after:w-10 flex items-center">
                <span className="mx-2 uppercase">{title}</span>
              </div>
            </div>
          </div>
        ) : null}
        <Banner
          backgroundImage={banner}
          marginBottom={marginBottom || "-27%"}
          marginTop={marginTop || "0"}
        >
          <div className="flex flex-col gap-y-7 md:flex-row items-center container lg:px-4 px-0 h-full lg:min-h-72 min-h-44 lg:py-10">
            <div className="lg:w-8/12 w-9/12 flex items-center h-full ">
              <img
                alt={
                  !Array.isArray(catalogs[selectedIndex]?.cover?.data)
                    ? catalogs[selectedIndex]?.cover?.data?.alt || ""
                    : ""
                }
                className="lg:h-64 h-38 lg:max-w-56 lg:min-w-56 max-w-28 min-w-28 rounded-md"
                onError={(e) => {
                  e.currentTarget.src = "/images/placeholder.png";
                }}
                src={
                  !Array.isArray(catalogs[selectedIndex]?.cover?.data)
                    ? catalogs[selectedIndex]?.cover?.data?.url
                    : ""
                }
              />
              <div className="lg:px-4 px-2">
                <div className="font-helvetica rtl:font-doran font-[100] lg:text-3xl text-xl leading-normal text-white mb-4 rtl:font-medium">
                  {catalogs[selectedIndex]?.title}
                </div>
                <div className="flex ltr:flex-row-reverse ltr:justify-end items-center">
                  <div className="lg:w-4/12 text-white lg:text-4xl rtl:text-xl font-thin ml-2">
                    {!Array.isArray(catalogs[selectedIndex]?.file?.data) ? (
                      <Dialog>
                        <DialogTrigger asChild></DialogTrigger>
                        <DialogContent className="max-w-[900px]">
                          <DialogHeader>
                            <DialogTitle>
                              {catalogs[selectedIndex]?.title}
                            </DialogTitle>
                            <DialogDescription>
                              {catalogs[selectedIndex]?.description}
                            </DialogDescription>
                            <object
                              data={catalogs[selectedIndex]?.file?.data?.url}
                              height="100%"
                              type="application/pdf"
                              width="100%"
                            >
                              <iframe
                                height="100%"
                                src={catalogs[selectedIndex]?.file?.data?.url}
                                style={{ border: "none" }}
                                title={catalogs[selectedIndex]?.title}
                                width="100%"
                              >
                                <p>
                                  Your browser does not support PDFs.
                                  <a
                                    href={
                                      catalogs[selectedIndex]?.file?.data?.url
                                    }
                                  >
                                    Download the PDF
                                  </a>
                                  .
                                </p>
                              </iframe>
                            </object>{" "}
                          </DialogHeader>
                        </DialogContent>
                      </Dialog>
                    ) : // <a
                    //   className="mb-6 flex items-center hover:text-primary cursor-pointer"
                    //   href={catalogs[selectedIndex].file.data.url}
                    //   rel="noreferrer"
                    //   target="_blank"
                    // >
                    //   <div>
                    //     <Search />
                    //   </div>
                    //   <span className="lg:mx-5 font-helvetica rtl:font-doran font-[100] text-lg lg:text-xl rtl:font-medium">
                    //     {view}
                    //   </span>
                    // </a>
                    null}
                    {!Array.isArray(catalogs[selectedIndex]?.file?.data) ? (
                      <a
                        className="flex items-center  hover:text-primary cursor-pointer"
                        href={catalogs[selectedIndex]?.file?.data?.url}
                        rel="noreferrer"
                        target="_blank"
                      >
                        <div>
                          <Download />
                        </div>
                        <span className="lg:mx-5 font-helvetica rtl:font-doran font-[100] text-lg lg:text-xl rtl:font-medium">
                          {download}
                        </span>
                      </a>
                    ) : null}
                  </div>
                  <div className="flex ltr:flex-row-reverse ltr:justify-end">
                    <button
                      className="text-white p-1 rounded-full outline-none border-none hover:text-primary"
                      onClick={setNextProject}
                      type="button"
                    >
                      <ChevronRightIcon className="lg:size-14 size-10" />
                    </button>
                    <button
                      className="text-white p-1 rounded-full outline-none border-none hover:text-primary"
                      onClick={setPreviousProject}
                      type="button"
                    >
                      <ChevronLeftIcon className="lg:size-14 size-10" />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </Banner>
      </div>
    );
  }
);
